import React, { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import TextLink from 'LuxKit/TextLink'
import ModalContext from 'contexts/ModalContext'
import TourExperienceModal from 'components/OfferPage/TourV2/Itinerary/TourExperienceModal'
import type { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import BodyText from 'LuxKit/Typography/BodyText'

const Root = styled(Group)`
  padding: ${rem(20)};
  border: 1px solid ${props => props.theme.palette.neutral.default.six};
`

interface MappedProps {
  selectedOccupancies: Array<App.Occupants>
}

interface Props {
  tourExperience: App.Tours.TourExperience
  title: string
}

function TourItineraryExperienceCard(props: Props & MappedProps) {
  const { tourExperience, title, selectedOccupancies } = props
  const intl = useIntl()
  const countOccupants = useMemo(() => sumUpOccupancies(selectedOccupancies), [selectedOccupancies])

  const showModal = useContext(ModalContext)
  const toggleModal = useCallback(() => {
    showModal(<TourExperienceModal tourExperience={tourExperience} title={title} countOccupants={countOccupants} />)
  }, [showModal, tourExperience, title, countOccupants])

  return <Root direction="horizontal" horizontalAlign="space-between" gap={16} wrap="wrap">
    <Heading variant="heading6">{tourExperience.name}</Heading>
    <Group fullWidth direction="horizontal" horizontalAlign="space-between">
      {tourExperience.price > 0 && <PriceRowPrice size="S" price={tourExperience.price} saleUnit="person" />}
      {tourExperience.price === 0 && <BodyText variant="medium" weight="bold">{intl.formatMessage({
        id: 'zXZGIDaTw4',
        defaultMessage: 'Included',
        description: 'Label indicating the experience is included in the tour price',
      })}
      </BodyText>}
      <TextLink size="small" onClick={toggleModal}>{intl.formatMessage({
        id: 'BhZ+bDTAYn',
        defaultMessage: 'View details',
        description: 'Hyperlink text to view experience details in a modal',
      })}
      </TextLink>
    </Group>
  </Root>
}

const mapOfferPageStateToProps = (offerPageState: OfferPageState): MappedProps => {
  return {
    selectedOccupancies: offerPageState.rooms,
  }
}

export default withOfferPageAndReduxState(mapOfferPageStateToProps, TourItineraryExperienceCard)
